import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  Button,
  ProgressBar,
} from 'react-native-paper';
import { LineChart, BarChart } from 'react-native-chart-kit';
import { ConfidenceIndicator } from './ConfidenceIndicator';

const { width: screenWidth } = Dimensions.get('window');

export interface LearningEfficiencyData {
  userId: string;
  timeRange: '7d' | '30d' | '90d' | '1y';
  overallEfficiency: number; // 0-1
  metrics: EfficiencyMetric[];
  trends: EfficiencyTrend[];
  optimizations: OptimizationSuggestion[];
  patterns: LearningPattern[];
  predictions: EfficiencyPrediction[];
  lastUpdated: string;
}

export interface EfficiencyMetric {
  id: string;
  name: string;
  category: 'time' | 'engagement' | 'retention' | 'application' | 'progress';
  value: number; // 0-1
  trend: 'improving' | 'stable' | 'declining';
  change: number; // percentage change
  benchmark: number; // average for similar users
  description: string;
  aiInsights: string[];
}

export interface EfficiencyTrend {
  metricId: string;
  timePoints: TimePoint[];
  trendDirection: 'up' | 'down' | 'stable';
  velocity: number; // rate of change
  confidence: number;
  seasonality?: SeasonalPattern;
}

export interface TimePoint {
  timestamp: string;
  value: number;
  context?: string; // what was happening at this time
}

export interface OptimizationSuggestion {
  id: string;
  category: 'schedule' | 'content' | 'method' | 'environment' | 'habits';
  title: string;
  description: string;
  potentialImprovement: number; // expected efficiency gain
  implementationDifficulty: 'easy' | 'medium' | 'hard';
  timeToSeeResults: number; // days
  confidence: number;
  aiReasoning: string;
  actionSteps: ActionStep[];
  relatedMetrics: string[];
}

export interface ActionStep {
  id: string;
  title: string;
  description: string;
  estimatedTime: number; // minutes
  priority: number;
  completed?: boolean;
}

export interface LearningPattern {
  id: string;
  type: 'temporal' | 'content' | 'behavioral' | 'environmental';
  name: string;
  description: string;
  strength: number; // how strong this pattern is
  impact: number; // how much it affects efficiency
  examples: string[];
  recommendations: string[];
}

export interface EfficiencyPrediction {
  metricId: string;
  predictedValue: number;
  timeframe: number; // days into future
  confidence: number;
  factors: PredictionFactor[];
}

export interface PredictionFactor {
  name: string;
  impact: number; // -1 to 1
  confidence: number;
}

export interface SeasonalPattern {
  type: 'daily' | 'weekly' | 'monthly';
  peaks: number[]; // hours/days/weeks when efficiency is highest
  troughs: number[]; // when efficiency is lowest
  amplitude: number; // how much variation
}

interface LearningEfficiencyTrackerProps {
  efficiencyData: LearningEfficiencyData;
  onOptimizationApply: (suggestion: OptimizationSuggestion) => void;
  onPatternExplore: (pattern: LearningPattern) => void;
  onMetricDrillDown: (metricId: string) => void;
  style?: any;
}

type ViewMode = 'overview' | 'trends' | 'optimizations' | 'patterns';
type TimeRange = '7d' | '30d' | '90d' | '1y';

export function LearningEfficiencyTracker({
  efficiencyData,
  onOptimizationApply,
  onPatternExplore,
  onMetricDrillDown,
  style,
}: LearningEfficiencyTrackerProps) {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(
    efficiencyData.timeRange
  );
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(
    new Set()
  );

  // Calculate efficiency score color
  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 0.8) return '#4CAF50';
    if (efficiency >= 0.6) return '#8BC34A';
    if (efficiency >= 0.4) return '#FFC107';
    if (efficiency >= 0.2) return '#FF9800';
    return '#F44336';
  };

  // Get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'trending-up';
      case 'declining':
        return 'trending-down';
      case 'stable':
        return 'trending-neutral';
      default:
        return 'help-circle';
    }
  };

  // Get trend color
  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
        return '#4CAF50';
      case 'declining':
        return '#F44336';
      case 'stable':
        return '#9E9E9E';
      default:
        return theme.colors.outline;
    }
  };

  const handleSuggestionToggle = useCallback((suggestionId: string) => {
    setExpandedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(suggestionId)) {
        newSet.delete(suggestionId);
      } else {
        newSet.add(suggestionId);
      }
      return newSet;
    });
  }, []);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!selectedMetric) return null;

    const trend = efficiencyData.trends.find(
      t => t.metricId === selectedMetric
    );
    if (!trend) return null;

    const labels = trend.timePoints.map(tp => {
      const date = new Date(tp.timestamp);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    const data = trend.timePoints.map(tp => tp.value * 100);

    return {
      labels,
      datasets: [
        {
          data,
          color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
          strokeWidth: 2,
        },
      ],
    };
  }, [selectedMetric, efficiencyData.trends]);

  const renderOverview = () => (
    <ScrollView style={styles.overviewContainer}>
      {/* Overall Efficiency Score */}
      <Card style={styles.efficiencyScoreCard}>
        <View style={styles.scoreHeader}>
          <Text
            variant='headlineLarge'
            style={[
              styles.efficiencyScore,
              { color: getEfficiencyColor(efficiencyData.overallEfficiency) },
            ]}
          >
            {Math.round(efficiencyData.overallEfficiency * 100)}%
          </Text>
          <Text variant='titleLarge' style={styles.scoreLabel}>
            Learning Efficiency
          </Text>
          <Text variant='bodyMedium' style={styles.scoreDescription}>
            Based on time optimization, engagement, retention, and progress
          </Text>
        </View>
      </Card>

      {/* Metrics Grid */}
      <View style={styles.metricsGrid}>
        {efficiencyData.metrics.map(metric => (
          <TouchableOpacity
            key={metric.id}
            style={styles.metricCard}
            onPress={() => onMetricDrillDown(metric.id)}
          >
            <Card style={styles.metricCardContent}>
              <View style={styles.metricHeader}>
                <Text variant='titleSmall' style={styles.metricName}>
                  {metric.name}
                </Text>
                <IconButton
                  icon={getTrendIcon(metric.trend)}
                  size={16}
                  iconColor={getTrendColor(metric.trend)}
                />
              </View>

              <Text
                variant='headlineSmall'
                style={[
                  styles.metricValue,
                  { color: getEfficiencyColor(metric.value) },
                ]}
              >
                {Math.round(metric.value * 100)}%
              </Text>

              <View style={styles.metricChange}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.changeText,
                    { color: metric.change >= 0 ? '#4CAF50' : '#F44336' },
                  ]}
                >
                  {metric.change >= 0 ? '+' : ''}
                  {metric.change.toFixed(1)}%
                </Text>
                <Text variant='bodySmall' style={styles.benchmarkText}>
                  vs {Math.round(metric.benchmark * 100)}% avg
                </Text>
              </View>

              <ProgressBar
                progress={metric.value}
                color={getEfficiencyColor(metric.value)}
                style={styles.metricProgress}
              />
            </Card>
          </TouchableOpacity>
        ))}
      </View>

      {/* Top Insights */}
      <Card style={styles.insightsCard}>
        <Text variant='titleMedium' style={styles.insightsTitle}>
          Key Insights
        </Text>
        {efficiencyData.metrics
          .filter(m => m.aiInsights.length > 0)
          .slice(0, 3)
          .map(metric => (
            <View key={metric.id} style={styles.insightItem}>
              <IconButton
                icon='lightbulb'
                size={16}
                iconColor={theme.colors.primary}
              />
              <Text variant='bodyMedium' style={styles.insightText}>
                {metric.aiInsights[0]}
              </Text>
            </View>
          ))}
      </Card>
    </ScrollView>
  );

  const renderTrends = () => (
    <ScrollView style={styles.trendsContainer}>
      {/* Metric Selector */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.metricSelector}
      >
        {efficiencyData.metrics.map(metric => (
          <Chip
            key={metric.id}
            selected={selectedMetric === metric.id}
            onPress={() => setSelectedMetric(metric.id)}
            style={styles.metricChip}
          >
            {metric.name}
          </Chip>
        ))}
      </ScrollView>

      {/* Chart */}
      {chartData && (
        <Card style={styles.chartCard}>
          <Text variant='titleMedium' style={styles.chartTitle}>
            {efficiencyData.metrics.find(m => m.id === selectedMetric)?.name}{' '}
            Trend
          </Text>
          <LineChart
            data={chartData}
            width={screenWidth - 64}
            height={220}
            chartConfig={{
              backgroundColor: theme.colors.surface,
              backgroundGradientFrom: theme.colors.surface,
              backgroundGradientTo: theme.colors.surface,
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
              labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity * 0.7})`,
              style: {
                borderRadius: 16,
              },
              propsForDots: {
                r: '4',
                strokeWidth: '2',
                stroke: '#6750A4',
              },
            }}
            bezier
            style={styles.chart}
          />
        </Card>
      )}

      {/* Predictions */}
      <Card style={styles.predictionsCard}>
        <Text variant='titleMedium' style={styles.predictionsTitle}>
          Efficiency Predictions
        </Text>
        {efficiencyData.predictions.map(prediction => {
          const metric = efficiencyData.metrics.find(
            m => m.id === prediction.metricId
          );
          if (!metric) return null;

          return (
            <View key={prediction.metricId} style={styles.predictionItem}>
              <View style={styles.predictionHeader}>
                <Text variant='titleSmall' style={styles.predictionMetric}>
                  {metric.name}
                </Text>
                <ConfidenceIndicator
                  confidence={prediction.confidence}
                  size='small'
                  showLabel={false}
                />
              </View>
              <Text variant='bodyMedium' style={styles.predictionText}>
                Predicted to reach {Math.round(prediction.predictedValue * 100)}
                % in {prediction.timeframe} days
              </Text>
              <View style={styles.predictionFactors}>
                {prediction.factors.slice(0, 2).map(factor => (
                  <Text
                    key={factor.name}
                    variant='bodySmall'
                    style={[
                      styles.factorText,
                      { color: factor.impact > 0 ? '#4CAF50' : '#F44336' },
                    ]}
                  >
                    {factor.impact > 0 ? '+' : ''}
                    {Math.round(factor.impact * 100)}% {factor.name}
                  </Text>
                ))}
              </View>
            </View>
          );
        })}
      </Card>
    </ScrollView>
  );

  const renderOptimizations = () => (
    <ScrollView style={styles.optimizationsContainer}>
      {efficiencyData.optimizations.map(suggestion => {
        const isExpanded = expandedSuggestions.has(suggestion.id);

        return (
          <Card key={suggestion.id} style={styles.suggestionCard}>
            <TouchableOpacity
              onPress={() => handleSuggestionToggle(suggestion.id)}
              style={styles.suggestionHeader}
            >
              <View style={styles.suggestionHeaderContent}>
                <View style={styles.suggestionInfo}>
                  <Text variant='titleMedium' style={styles.suggestionTitle}>
                    {suggestion.title}
                  </Text>
                  <Text
                    variant='bodyMedium'
                    style={styles.suggestionDescription}
                  >
                    {suggestion.description}
                  </Text>
                  <View style={styles.suggestionMeta}>
                    <Text variant='bodySmall' style={styles.improvementText}>
                      +{Math.round(suggestion.potentialImprovement * 100)}%
                      efficiency
                    </Text>
                    <Chip
                      compact
                      style={[
                        styles.difficultyChip,
                        {
                          backgroundColor:
                            suggestion.implementationDifficulty === 'easy'
                              ? '#4CAF50'
                              : suggestion.implementationDifficulty === 'medium'
                                ? '#FF9800'
                                : '#F44336',
                        },
                      ]}
                    >
                      {suggestion.implementationDifficulty}
                    </Chip>
                    <ConfidenceIndicator
                      confidence={suggestion.confidence}
                      size='small'
                      showLabel={false}
                    />
                  </View>
                </View>
                <IconButton
                  icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                  size={20}
                />
              </View>
            </TouchableOpacity>

            {isExpanded && (
              <View style={styles.suggestionDetails}>
                {/* AI Reasoning */}
                <View style={styles.reasoningSection}>
                  <Text variant='labelLarge' style={styles.sectionTitle}>
                    AI Analysis
                  </Text>
                  <Text variant='bodyMedium' style={styles.reasoningText}>
                    {suggestion.aiReasoning}
                  </Text>
                </View>

                {/* Action Steps */}
                <View style={styles.actionStepsSection}>
                  <Text variant='labelLarge' style={styles.sectionTitle}>
                    Action Steps
                  </Text>
                  {suggestion.actionSteps.map((step, index) => (
                    <View key={step.id} style={styles.actionStep}>
                      <View style={styles.stepNumber}>
                        <Text
                          variant='labelSmall'
                          style={styles.stepNumberText}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <View style={styles.stepContent}>
                        <Text variant='titleSmall' style={styles.stepTitle}>
                          {step.title}
                        </Text>
                        <Text
                          variant='bodySmall'
                          style={styles.stepDescription}
                        >
                          {step.description}
                        </Text>
                        <Text variant='bodySmall' style={styles.stepTime}>
                          ~{step.estimatedTime} minutes
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>

                {/* Apply Button */}
                <Button
                  mode='contained'
                  onPress={() => onOptimizationApply(suggestion)}
                  style={styles.applyButton}
                >
                  Apply This Optimization
                </Button>
              </View>
            )}
          </Card>
        );
      })}
    </ScrollView>
  );

  const renderPatterns = () => (
    <ScrollView style={styles.patternsContainer}>
      {efficiencyData.patterns.map(pattern => (
        <Card key={pattern.id} style={styles.patternCard}>
          <TouchableOpacity
            onPress={() => onPatternExplore(pattern)}
            style={styles.patternContent}
          >
            <View style={styles.patternHeader}>
              <Text variant='titleMedium' style={styles.patternName}>
                {pattern.name}
              </Text>
              <View style={styles.patternStrength}>
                <Text variant='bodySmall' style={styles.strengthLabel}>
                  Strength
                </Text>
                <ProgressBar
                  progress={pattern.strength}
                  color={getEfficiencyColor(pattern.strength)}
                  style={styles.strengthBar}
                />
              </View>
            </View>

            <Text variant='bodyMedium' style={styles.patternDescription}>
              {pattern.description}
            </Text>

            <View style={styles.patternImpact}>
              <Text
                variant='bodySmall'
                style={[
                  styles.impactText,
                  { color: pattern.impact > 0 ? '#4CAF50' : '#F44336' },
                ]}
              >
                {pattern.impact > 0 ? '+' : ''}
                {Math.round(pattern.impact * 100)}% efficiency impact
              </Text>
            </View>

            {/* Examples */}
            <View style={styles.patternExamples}>
              <Text variant='labelMedium' style={styles.examplesTitle}>
                Examples:
              </Text>
              {pattern.examples.slice(0, 2).map((example, index) => (
                <Text
                  key={index}
                  variant='bodySmall'
                  style={styles.exampleText}
                >
                  • {example}
                </Text>
              ))}
            </View>

            <IconButton
              icon='chevron-right'
              size={16}
              style={styles.patternArrow}
            />
          </TouchableOpacity>
        </Card>
      ))}
    </ScrollView>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'overview':
        return renderOverview();
      case 'trends':
        return renderTrends();
      case 'optimizations':
        return renderOptimizations();
      case 'patterns':
        return renderPatterns();
      default:
        return renderOverview();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Learning Efficiency
        </Text>

        {/* Tab Navigation */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabNavigation}
        >
          {[
            { key: 'overview', label: 'Overview', icon: 'view-dashboard' },
            { key: 'trends', label: 'Trends', icon: 'chart-line' },
            { key: 'optimizations', label: 'Optimize', icon: 'tune' },
            { key: 'patterns', label: 'Patterns', icon: 'pattern' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, viewMode === tab.key && styles.activeTab]}
              onPress={() => setViewMode(tab.key as ViewMode)}
            >
              <IconButton
                icon={tab.icon}
                size={16}
                iconColor={
                  viewMode === tab.key ? '#6750A4' : theme.colors.onSurface
                }
              />
              <Text
                variant='labelMedium'
                style={[
                  styles.tabLabel,
                  viewMode === tab.key && styles.activeTabLabel,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      {renderContent()}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 16,
    fontWeight: '600',
  },
  tabNavigation: {
    flexDirection: 'row',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  activeTab: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  tabLabel: {
    marginLeft: 4,
  },
  activeTabLabel: {
    color: '#6750A4',
    fontWeight: '600',
  },

  // Overview Styles
  overviewContainer: {
    flex: 1,
    padding: 16,
  },
  efficiencyScoreCard: {
    padding: 24,
    marginBottom: 16,
    alignItems: 'center',
  },
  scoreHeader: {
    alignItems: 'center',
  },
  efficiencyScore: {
    fontSize: 64,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  scoreLabel: {
    fontWeight: '600',
    marginBottom: 8,
  },
  scoreDescription: {
    textAlign: 'center',
    opacity: 0.7,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  metricCard: {
    width: (screenWidth - 64) / 2,
  },
  metricCardContent: {
    padding: 16,
  },
  metricHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  metricName: {
    flex: 1,
    fontWeight: '600',
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  metricChange: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  changeText: {
    fontWeight: '600',
  },
  benchmarkText: {
    opacity: 0.7,
  },
  metricProgress: {
    height: 4,
  },
  insightsCard: {
    padding: 16,
  },
  insightsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  insightText: {
    flex: 1,
    marginLeft: 8,
    lineHeight: 20,
  },

  // Trends Styles
  trendsContainer: {
    flex: 1,
    padding: 16,
  },
  metricSelector: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  metricChip: {
    marginRight: 8,
  },
  chartCard: {
    padding: 16,
    marginBottom: 16,
  },
  chartTitle: {
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  predictionsCard: {
    padding: 16,
  },
  predictionsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  predictionItem: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  predictionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  predictionMetric: {
    fontWeight: '600',
  },
  predictionText: {
    marginBottom: 8,
  },
  predictionFactors: {
    flexDirection: 'row',
    gap: 16,
  },
  factorText: {
    fontWeight: '600',
  },

  // Optimizations Styles
  optimizationsContainer: {
    flex: 1,
    padding: 16,
  },
  suggestionCard: {
    marginBottom: 12,
  },
  suggestionHeader: {
    padding: 16,
  },
  suggestionHeaderContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  suggestionInfo: {
    flex: 1,
  },
  suggestionTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  suggestionDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  suggestionMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  improvementText: {
    color: '#4CAF50',
    fontWeight: '600',
  },
  difficultyChip: {
    height: 24,
  },
  suggestionDetails: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  reasoningSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  reasoningText: {
    lineHeight: 20,
    fontStyle: 'italic',
  },
  actionStepsSection: {
    marginBottom: 16,
  },
  actionStep: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#6750A4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    color: 'white',
    fontWeight: 'bold',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  stepDescription: {
    lineHeight: 18,
    marginBottom: 4,
  },
  stepTime: {
    opacity: 0.7,
  },
  applyButton: {
    marginTop: 8,
  },

  // Patterns Styles
  patternsContainer: {
    flex: 1,
    padding: 16,
  },
  patternCard: {
    marginBottom: 12,
  },
  patternContent: {
    padding: 16,
    position: 'relative',
  },
  patternHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  patternName: {
    fontWeight: '600',
    flex: 1,
  },
  patternStrength: {
    alignItems: 'flex-end',
    minWidth: 80,
  },
  strengthLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  strengthBar: {
    width: 60,
    height: 4,
  },
  patternDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  patternImpact: {
    marginBottom: 12,
  },
  impactText: {
    fontWeight: '600',
  },
  patternExamples: {
    marginBottom: 8,
  },
  examplesTitle: {
    marginBottom: 4,
    fontWeight: '600',
  },
  exampleText: {
    opacity: 0.7,
    marginBottom: 2,
  },
  patternArrow: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
});
